// components/BillPaymentModal.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import { showToast } from './Toast';
import { formatCurrency } from '../utils';

const BillPaymentModal = ({ bill, payment, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    amount: payment ? payment.amount.toString() : bill.amount.toString(),
    payment_date: payment ? payment.payment_date : new Date().toISOString().split('T')[0],
    payment_account_id: '',
    notes: payment ? (payment.notes || '') : ''
  });
  const [accounts, setAccounts] = useState([]);
  const [budgetItem, setBudgetItem] = useState(null);

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await axios.get(`${API_BASE_PATH}/accounts`);
        const allAccounts = response.data || [];
        // Filter out credit card accounts (those come from debts)
        const regularAccounts = allAccounts.filter(a => a.type !== 'credit_card' && a.is_active);
        setAccounts(regularAccounts);

        // Auto-select first account if available
        if (regularAccounts.length > 0 && !formData.payment_account_id) {
          setFormData(prev => ({ ...prev, payment_account_id: regularAccounts[0].id }));
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };

    fetchAccounts();
  }, []);

  // Fetch the budget item if editing a payment
  useEffect(() => {
    if (payment) {
      const fetchBudgetItem = async () => {
        try {
          const response = await axios.get(`${API_BASE_PATH}/budget-items`);
          const items = response.data || [];
          const item = items.find(i =>
            i.type === 'bill_payment' &&
            i.bill_id === bill.id &&
            i.paid_date === payment.payment_date
          );
          setBudgetItem(item);
        } catch (error) {
          console.error('Error fetching budget item:', error);
        }
      };
      fetchBudgetItem();
    }
  }, [payment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      showToast('Please enter a valid amount', 'error');
      return;
    }

    if (!formData.payment_account_id) {
      showToast('Please select a payment account', 'error');
      return;
    }

    try {
      const amount = parseFloat(formData.amount);

      if (payment) {
        // Editing existing payment - delete old one and create new one
        // (since payment_date is part of the key, we can't update it directly)
        await axios.delete(`${API_BASE_PATH}/bill-payments/${bill.id}/${payment.payment_date}`);

        if (budgetItem) {
          await axios.delete(`${API_BASE_PATH}/budget-items/${budgetItem.id}`);
        }
      }

      // Create/recreate the payment in bill_payments table
      // This will automatically create a transaction with the bill's budget_id
      await axios.post(`${API_BASE_PATH}/bill-payments`, {
        bill_id: bill.id,
        amount,
        payment_date: formData.payment_date,
        payment_account_id: formData.payment_account_id,
        notes: formData.notes.trim() || null
      });

      showToast(payment ? 'Payment updated successfully' : 'Payment recorded successfully', 'success');
      onSave();
    } catch (error) {
      console.error('Error saving payment:', error);
      showToast(error.response?.data?.message || 'Failed to save payment', 'error');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            <i className="fas fa-dollar-sign"></i>
            {payment ? 'Edit Bill Payment' : 'Record Bill Payment'}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{
              padding: '12px',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--border-radius)',
              marginBottom: '16px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Bill
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600 }}>
                {bill.name}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                Regular amount: {formatCurrency(bill.amount)}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Payment Amount <span style={{ color: 'var(--danger-color)' }}>*</span>
              </label>
              <input
                type="number"
                name="amount"
                className="form-control"
                placeholder="0.00"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={handleChange}
                autoFocus
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Payment Date <span style={{ color: 'var(--danger-color)' }}>*</span>
              </label>
              <input
                type="date"
                name="payment_date"
                className="form-control"
                value={formData.payment_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Pay From Account <span style={{ color: 'var(--danger-color)' }}>*</span>
              </label>
              <select
                name="payment_account_id"
                className="form-control"
                value={formData.payment_account_id}
                onChange={handleChange}
                required
              >
                <option value="">Select Account</option>
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.type}) - {formatCurrency(account.current_balance)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                name="notes"
                className="form-control"
                placeholder="Optional notes about this payment"
                rows="3"
                value={formData.notes}
                onChange={handleChange}
              />
            </div>

            {bill.next_due_date && (
              <div style={{
                padding: '12px',
                background: 'var(--info-light)',
                border: '1px solid var(--info-color)',
                borderRadius: 'var(--border-radius)',
                fontSize: '12px',
                color: 'var(--text-primary)'
              }}>
                <i className="fas fa-info-circle" style={{ marginRight: '6px', color: 'var(--info-color)' }}></i>
                After recording this payment, the next due date will automatically advance based on the bill's frequency ({bill.frequency}).
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              <i className="fas fa-check"></i>
              {payment ? 'Update Payment' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillPaymentModal;